import type { ButtonBlock } from "@aswin.dev/types";
import type { RenderContext } from "../render-context";
import { escapeAttr, escapeHtml } from "../escape";
import { toPaddingString } from "../padding";
import { bgAttr } from "../utils";
import { isHiddenOnAll, getCssClasses } from "../visibility";

/**
 * Render a button block to MJML markup.
 */
export function renderButton(
  block: ButtonBlock,
  context: RenderContext,
): string {
  if (isHiddenOnAll(block)) {
    return "";
  }

  const padding = toPaddingString(block.styles.padding);
  const bgColor = bgAttr(block.styles.backgroundColor, "container");
  const buttonPadding = toPaddingString(block.buttonPadding);

  // Omit href entirely when empty so we don't emit `<a href="">`.
  const href = resolveButtonHref(block);
  const hrefAttr = href === "" ? "" : ` href="${href}"`;
  const backgroundColor = escapeAttr(block.backgroundColor);
  const textColor = escapeAttr(block.textColor);
  const fontSize = block.fontSize;
  const borderRadius = block.borderRadius;
  const text = escapeHtml(block.text);
  const action = block.clickAction ?? "none";
  const targetAttr =
    block.openInNewTab && action !== "next_step" && action !== "previous_step"
      ? ' target="_blank" rel="noopener"'
      : "";
  const fontFamilyAttr = renderFontFamilyAttr(block.fontFamily, context);
  const btnCssClassAttr = renderMjButtonCssClassAttr(block);
  const borderAttrs = renderMjButtonBorderAttrs(block);

  return `<mj-button${hrefAttr}${targetAttr}
  background-color="${backgroundColor}"
  color="${textColor}"
  font-size="${fontSize}px"
  font-weight="bold"
  border-radius="${borderRadius}px"
  inner-padding="${buttonPadding}"
  padding="${padding}"${bgColor}${fontFamilyAttr}${btnCssClassAttr}${borderAttrs}
>${text}</mj-button>`;
}

/** Merges visibility classes with interaction hooks (e.g. multi-step popup). */
function renderMjButtonCssClassAttr(block: ButtonBlock): string {
  const parts: string[] = [];
  const vis = getCssClasses(block);
  if (vis) {
    parts.push(...vis.split(/\s+/).filter(Boolean));
  }
  const action = block.clickAction ?? "none";
  if (action === "next_step") {
    parts.push("tpl-btn-next-step");
  } else if (action === "previous_step") {
    parts.push("tpl-btn-prev-step");
  }
  if (parts.length === 0) {
    return "";
  }
  return ` css-class="${escapeAttr(parts.join(" "))}"`;
}

/** MJML `mj-button` accepts full CSS border strings per side. */
function renderMjButtonBorderAttrs(block: ButtonBlock): string {
  const w = block.borderWidth ?? 0;
  if (w <= 0) {
    return "";
  }
  const color = block.borderColor ?? "#000000";
  const segment = `${w}px solid ${color}`;
  const escSegment = escapeAttr(segment);
  const escNone = escapeAttr("none");
  const sides = block.borderSides ?? "all";

  if (sides === "all") {
    return ` border="${escSegment}"`;
  }

  const top = sides === "top" ? escSegment : escNone;
  const right = sides === "right" ? escSegment : escNone;
  const bottom = sides === "bottom" ? escSegment : escNone;
  const left = sides === "left" ? escSegment : escNone;

  return ` border-top="${top}" border-right="${right}" border-bottom="${bottom}" border-left="${left}"`;
}

function resolveButtonHref(block: ButtonBlock): string {
  const action = block.clickAction ?? "none";

  if (action === "scroll_anchor") {
    const raw = block.clickActionAnchorId?.trim().replace(/^#/, "") ?? "";
    if (raw !== "") {
      return escapeAttr(`#${raw}`);
    }
    return block.url === "" ? "" : escapeAttr(block.url);
  }

  if (action === "none" || action === "load_page") {
    return block.url === "" ? "" : escapeAttr(block.url);
  }

  // next_step / previous_step / close_popup: `#` keeps the control clickable in HTML; optional URL fallback.
  if (
    action === "next_step" ||
    action === "previous_step" ||
    action === "close_popup"
  ) {
    return escapeAttr(block.url === "" ? "#" : block.url);
  }

  return "";
}

function renderFontFamilyAttr(
  fontFamily: string | undefined,
  context: RenderContext,
): string {
  if (!fontFamily) {
    return "";
  }

  const resolved = context.resolveFontFamily(fontFamily);

  return ` font-family="${resolved}"`;
}
